/********* STORE FOR ALL GLOBAL UI STATES *********/
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { AdminReportFilters, Locale, UIState } from "../types/ui-types";
import { createRef } from "react";
import { Toast } from "primereact/toast";

/* Example for sidepanel state */
const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      locale: Locale.FI,
      setLocale: (locale: Locale) => set(() => ({ locale: locale })),
      bearerToken: undefined,
      setBearerToken: (token?: string) => set(() => ({ bearerToken: token })),
      azureAdCode: undefined,
      setAzureAdCode: (code?: string) => set(() => ({ azureAdCode: code })),
      isLoggedIn: false,
      setIsLoggedIn: (isLoggedIn: boolean) =>
        set(() => ({ isLoggedIn: isLoggedIn })),
      sessionExpired: false,
      setSessionExpired: (sessionExpired: boolean) =>
        set(() => ({ sessionExpired })),
      postLoginRedirectPath: null,
      setPostLoginRedirectPath: (postLoginRedirectPath: string | null) =>
        set(() => ({ postLoginRedirectPath })),
      toastRef: createRef<Toast>(),
      companyId: null,
      setCompanyId: (companyId: number | null) => set(() => ({ companyId })),
      companyName: null,
      setCompanyName: (companyName: string | null) =>
        set(() => ({ companyName })),
      setOrganizationId: (organizationId: number | null) =>
        set(() => ({ organizationId })),
      organizationId: null,
      organizationName: null,
      setOrganizationName: (organizationName: string | null) =>
        set(() => ({ organizationName })),
      // weights per year, year functioning as a key
      ecomodMaxWeights: [],
      setEcomodMaxWeights: (
        ecomodMaxWeights: { year: number; value: number }[],
      ) => set(() => ({ ecomodMaxWeights: ecomodMaxWeights })),
      tempToken: undefined,
      setTempToken: (token?: string) => set(() => ({ tempToken: token })),
      tempUserId: undefined,
      setTempUserId: (userId?: number) => set(() => ({ tempUserId: userId })),
      selectedYearForNewReport: null,
      setSelectedYearForNewReport: (year: number | null) =>
        set(() => ({ selectedYearForNewReport: year })),
      selectedQuarterForNewReport: null,
      setSelectedQuarterForNewReport: (quarter: 1 | 2 | 3 | 4 | null) =>
        set(() => ({ selectedQuarterForNewReport: quarter })),
      selectedMonthForNewReport: null,
      setSelectedMonthForNewReport: (month: number | null) =>
        set(() => ({ selectedMonthForNewReport: month })),
      theme: "dark",
      setTheme: (theme: "light" | "dark") => set(() => ({ theme: theme })),
      selectedAdminFilters: {
        company: undefined,
        skip: 0,
        take: 50,
        year: undefined,
        quarter: undefined,
        month: undefined,
        requiresApproval: false,
      },
      setSelectedAdminFilters: (filters: AdminReportFilters) =>
        set(() => ({ selectedAdminFilters: filters })),
    }),
    {
      name: "ui-store",
      // use local storage to persist the store
      // with session storage, multiple tabs didn't work properly
      storage: createJSONStorage(() => localStorage),
      // Partialize the store to only store the items listed in here to local storage
      partialize: (state) => ({
        locale: state.locale,
        bearerToken: state.bearerToken,
        companyId: state.companyId,
        companyName: state.companyName,
        organizationId: state.organizationId,
        organizationName: state.organizationName,
        tempToken: state.tempToken,
        tempUserId: state.tempUserId,
        selectedYearForNewReport: state.selectedYearForNewReport,
        selectedQuarterForNewReport: state.selectedQuarterForNewReport,
        selectedMonthForNewReport: state.selectedMonthForNewReport,
        theme: state.theme,
        selectedAdminFilters: state.selectedAdminFilters,
        // persisted so the dialog/redirect survive the reload triggered by logout()
        sessionExpired: state.sessionExpired,
        postLoginRedirectPath: state.postLoginRedirectPath,
      }),
    },
  ),
);

export { useUIStore };
